import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";

interface Vehicle {
  id: number;
  regNo: string;
  customerId: number;
  vehicleDetails: string;
  customer: {
    id: number;
    name: string;
    phone: string;
  };
}

export default function VehiclesList() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getVehicles();
  }, [page, limit, search]);

  const getVehicles = async () => {
    setLoading(true);
    try {
      const response = await request.get("/api/getVehicles", {
        search,
        limit,
        offset: page,
      });
      setVehicles(response.data.data);
      setTotalCount(response.data.count);
    } catch {
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "regNo",
      label: "Reg No",
      minWidth: 150,
      format: (value) => value,
    },
    {
      id: "customer",
      label: "Customer",
      minWidth: 180,
      format: (_value, row) =>
        row.customer?.name
          ? `${row.customer.name}${row.customer.phone ? ` (${row.customer.phone})` : ""}`
          : "-",
    },
    {
      id: "vehicleDetails",
      label: "Vehicle Details",
      minWidth: 200,
      format: (value) => value || "-",
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 80,
      format: (_value, row) => (
        <ActionIcons actions={[
          { icon: <EditIcon />, tooltip: "Edit", onClick: () => navigate(`/admin/vehicles/${row.id}`) },
        ]} />
      ),
    },
  ];

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Vehicles"
        showSearch
        searchPlaceholder="Search vehicles..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Vehicle",
          icon: <AddIcon />,
          onClick: () => navigate("/admin/vehicles/create"),
        }}
      />
      <DataTable
        columns={columns}
        rows={vehicles}
        totalCount={totalCount}
        onPaginationChange={handlePaginationChange}
        page={page}
        limit={limit}
      />
    </PageWrapper>
  );
}
